import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { trackMiniAppEvent } from '../../utils/analytics';
import {
  engineShell,
  titleStyle,
  instructionsStyle,
  feedbackCorrect,
  feedbackIncorrect,
  secondaryBtn,
} from './shared/engineTheme';
import { useCelebration, CelebrationOverlay, A11yAnnouncement } from './shared/Celebration';
import { useGuidedHints } from './shared/useGuidedHints';
import GuidedHintOverlay, {
  GuidedHintHelpButton,
  guidedHintReadAloud,
} from './shared/GuidedHintOverlay';
import { TAP_ON_IMAGE_ONE_SHOT } from './shared/engineInteractionOneShots';
import { isTapFirstLessonUi } from './shared/lessonUiMode';

/**
 * Tap-on-image: kid identifies right things in an SVG scene by tapping hotspots.
 *
 * Content:
 * {
 *   instructions?: string,
 *   scene: {
 *     viewBox?: '0 0 200 120',
 *     background?: [{ type: 'rect'|'circle'|'path', ... }],
 *     decorations?: [{ type: 'text'|'emoji', x, y, content, fontSize? }],
 *   },
 *   hotspots: [
 *     {
 *       id, label,
 *       shape?: 'circle'|'rect',     // default 'circle'
 *       cx, cy, r,                   // for circle
 *       x, y, width, height,         // for rect
 *       correct: boolean,
 *       feedback?: string,
 *       emoji?: string,              // optional small icon shown over the hotspot
 *     },
 *   ],
 *   goal?: { type?: 'find_all_correct' | 'count', count?: number },
 *   victory?: { message?: string },
 * }
 *
 * onComplete payload:
 *   { activityId, engine: 'tap_on_image', perfect: boolean, misses: number, correctTapped: number }
 */

const DEFAULT_VIEWBOX = '0 0 200 120';

/** Min % of scene width for circular hotspot diameter (child-friendly touch targets on phones). */
const MIN_CIRCLE_SIDE_PCT = 14;

function parseViewBox(vb) {
  const parts = String(vb || DEFAULT_VIEWBOX)
    .trim()
    .split(/\s+/)
    .map(Number);
  if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) return [0, 0, 200, 120];
  return parts;
}

function hotspotCenter(h) {
  if (h.shape === 'rect') {
    return { x: (h.x ?? 0) + (h.width ?? 0) / 2, y: (h.y ?? 0) + (h.height ?? 0) / 2 };
  }
  return { x: h.cx ?? 0, y: h.cy ?? 0 };
}

function SceneSvg({ scene, vbW, vbH }) {
  const background = scene?.background || [];
  const decorations = scene?.decorations || [];
  return (
    <svg
      viewBox={`0 0 ${vbW} ${vbH}`}
      preserveAspectRatio="none"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      aria-hidden
    >
      {background.map((b, i) => {
        if (b.type === 'rect') {
          return (
            <rect
              key={`bg-${i}`}
              x={b.x ?? 0}
              y={b.y ?? 0}
              width={b.width ?? vbW}
              height={b.height ?? vbH}
              fill={b.fill || '#e0f2fe'}
              opacity={b.opacity ?? 1}
            />
          );
        }
        if (b.type === 'circle') {
          return (
            <circle
              key={`bg-${i}`}
              cx={b.cx ?? 0}
              cy={b.cy ?? 0}
              r={b.r ?? 1}
              fill={b.fill || '#fde68a'}
              opacity={b.opacity ?? 1}
            />
          );
        }
        if (b.type === 'path' && typeof b.d === 'string') {
          return (
            <path
              key={`bg-${i}`}
              d={b.d}
              fill={b.fill || 'none'}
              stroke={b.stroke || 'none'}
              strokeWidth={b.strokeWidth ?? 1}
              opacity={b.opacity ?? 1}
            />
          );
        }
        return null;
      })}
      {decorations.map((d, i) =>
        d.type === 'text' || d.type === 'emoji' ? (
          <text
            key={`dec-${i}`}
            x={d.x ?? 0}
            y={d.y ?? 0}
            fontSize={d.fontSize ?? 6}
            textAnchor={d.textAnchor || 'middle'}
            fill={d.fill || 'currentColor'}
            opacity={d.opacity ?? 0.9}
          >
            {d.content}
          </text>
        ) : null,
      )}
    </svg>
  );
}

export default function TapOnImageEngine({
  title = 'Find them in the picture',
  content = {},
  hints = [],
  explanation,
  activityId = 'engine_tap_on_image',
  onProgress,
  onComplete,
  guidedHintsEnabled = true,
  onRegisterLessonFooterActions,
  lessonUiMode = 'default',
}) {
  const tapFirst = isTapFirstLessonUi(lessonUiMode);
  const scene = content.scene;
  const hotspots = useMemo(() => content.hotspots || [], [content.hotspots]);
  const instructions = content.instructions || '';
  const victoryMsg = content.victory?.message || 'Great looking!';
  const goalType = content.goal?.type || 'find_all_correct';
  const goalCount = Math.max(1, Number(content.goal?.count) || 1);

  const [vbX, vbY, vbW, vbH] = parseViewBox(scene?.viewBox);
  const aspect = vbW > 0 && vbH > 0 ? `${vbW} / ${vbH}` : '5 / 3';

  const [tapped, setTapped] = useState({}); // hotspotId -> 'correct' | 'wrong'
  const [missCount, setMissCount] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [a11yMsg, setA11yMsg] = useState('');
  const [showCelebration, celebrate] = useCelebration();
  const completedRef = useRef(false);
  const lessonShellFooter = typeof onRegisterLessonFooterActions === 'function';
  const [awaitContinueAfterRevealAnswer, setAwaitContinueAfterRevealAnswer] = useState(false);

  const correctHotspots = useMemo(() => hotspots.filter((h) => h.correct), [hotspots]);
  const correctTappedCount = useMemo(
    () => Object.entries(tapped).filter(([id, st]) => st === 'correct' && hotspots.find((h) => h.id === id)?.correct).length,
    [tapped, hotspots],
  );

  const goalReached =
    goalType === 'count'
      ? correctTappedCount >= Math.min(goalCount, correctHotspots.length)
      : correctHotspots.length > 0 && correctTappedCount >= correctHotspots.length;

  const getExplanation = useCallback(() => content.explanation, [content.explanation]);

  const guided = useGuidedHints({
    activityId,
    hints,
    oneShotInteractionHint: TAP_ON_IMAGE_ONE_SHOT,
    explanation,
    getExplanation,
    isActivityComplete: goalReached && !awaitContinueAfterRevealAnswer,
    enabled: guidedHintsEnabled,
  });

  const { noteUserAction, noteIncorrectAnswer, noteCorrectAnswer, resetSession } = guided;

  useEffect(() => {
    if (!goalReached || completedRef.current) return;
    if (lessonShellFooter && awaitContinueAfterRevealAnswer) return;
    completedRef.current = true;
    celebrate();
    noteCorrectAnswer();
    setFeedback({ type: 'correct', message: victoryMsg });
    setA11yMsg(victoryMsg);
    onComplete?.({
      activityId,
      engine: 'tap_on_image',
      perfect: missCount === 0,
      misses: missCount,
      correctTapped: correctTappedCount,
    });
    trackMiniAppEvent.completed(activityId);
  }, [
    goalReached,
    activityId,
    celebrate,
    missCount,
    noteCorrectAnswer,
    onComplete,
    victoryMsg,
    correctTappedCount,
    lessonShellFooter,
    awaitContinueAfterRevealAnswer,
  ]);

  const handleTap = (h) => {
    if (tapped[h.id]) return;
    noteUserAction();
    if (h.correct) {
      setTapped((t) => ({ ...t, [h.id]: 'correct' }));
      setFeedback({ type: 'correct', message: h.feedback || `Yes — ${h.label}.` });
      setA11yMsg(`Correct: ${h.label}.`);
      onProgress?.({
        activityId,
        engine: 'tap_on_image',
        phase: 'tap',
        hotspotId: h.id,
        correct: true,
      });
      trackMiniAppEvent.interaction(activityId, 'tap_correct');
    } else {
      noteIncorrectAnswer();
      setTapped((t) => ({ ...t, [h.id]: 'wrong' }));
      setMissCount((c) => c + 1);
      setFeedback({ type: 'wrong', message: h.feedback || `Not this one — ${h.label}.` });
      setA11yMsg(`Not this one: ${h.label}.`);
      onProgress?.({
        activityId,
        engine: 'tap_on_image',
        phase: 'tap',
        hotspotId: h.id,
        correct: false,
      });
      trackMiniAppEvent.interaction(activityId, 'tap_wrong');
      // wrong taps clear after a moment so user can re-attempt others
      setTimeout(() => {
        setTapped((cur) => {
          const { [h.id]: _, ...rest } = cur;
          return rest;
        });
      }, 700);
    }
  };

  const reset = () => {
    resetSession();
    completedRef.current = false;
    setAwaitContinueAfterRevealAnswer(false);
    setTapped({});
    setMissCount(0);
    setFeedback(null);
  };

  const revealAnswer = useCallback(() => {
    if (goalReached) return;
    noteUserAction();
    if (lessonShellFooter) {
      setAwaitContinueAfterRevealAnswer(true);
      setFeedback({
        type: 'correct',
        message:
          'Correct spots are highlighted. Tap Continue below when you are ready to go on.',
      });
      setA11yMsg('Answer shown. Tap Continue when you are ready.');
    }
    setTapped((cur) => {
      const next = { ...cur };
      for (const h of correctHotspots) {
        next[h.id] = 'correct';
      }
      return next;
    });
    trackMiniAppEvent.interaction(activityId, 'reveal_answer');
    onProgress?.({ activityId, engine: 'tap_on_image', phase: 'reveal_answer' });
  }, [goalReached, correctHotspots, noteUserAction, activityId, onProgress, lessonShellFooter]);

  useEffect(() => {
    if (!onRegisterLessonFooterActions) return undefined;

    if (
      lessonShellFooter &&
      scene &&
      Array.isArray(hotspots) &&
      hotspots.length > 0 &&
      goalReached &&
      awaitContinueAfterRevealAnswer &&
      !completedRef.current
    ) {
      onRegisterLessonFooterActions({
        continueLesson: () => {
          noteUserAction();
          setAwaitContinueAfterRevealAnswer(false);
          if (completedRef.current) return;
          completedRef.current = true;
          celebrate();
          noteCorrectAnswer();
          setFeedback({ type: 'correct', message: victoryMsg });
          setA11yMsg(victoryMsg);
          onComplete?.({
            activityId,
            engine: 'tap_on_image',
            perfect: missCount === 0,
            misses: missCount,
            correctTapped: correctTappedCount,
          });
          trackMiniAppEvent.completed(activityId);
        },
      });
      return () => onRegisterLessonFooterActions(null);
    }

    if (!scene || !Array.isArray(hotspots) || hotspots.length === 0 || goalReached) {
      onRegisterLessonFooterActions(null);
      return undefined;
    }
    onRegisterLessonFooterActions({ showAnswer: revealAnswer });
    return () => onRegisterLessonFooterActions(null);
  }, [
    onRegisterLessonFooterActions,
    lessonShellFooter,
    scene,
    hotspots,
    goalReached,
    awaitContinueAfterRevealAnswer,
    revealAnswer,
    noteUserAction,
    celebrate,
    noteCorrectAnswer,
    onComplete,
    missCount,
    activityId,
    victoryMsg,
    correctTappedCount,
  ]);

  if (!scene || !Array.isArray(hotspots) || hotspots.length === 0) {
    return (
      <div style={engineShell}>
        <p style={instructionsStyle}>This tap activity has no scene or hotspots yet.</p>
      </div>
    );
  }

  return (
    <div
      style={{
        ...engineShell,
        maxWidth: 560,
        position: 'relative',
        ...(guided.contentReserveStyle || {}),
        ...(tapFirst ? { overflow: 'hidden', minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column' } : {}),
      }}
    >
      <CelebrationOverlay visible={showCelebration} message={victoryMsg} />
      <A11yAnnouncement message={a11yMsg} />
      <GuidedHintOverlay
        open={guided.open}
        isNarrow={guided.isNarrow}
        visibleLevel={guided.visibleLevel}
        maxLevel={guided.maxLevel}
        isInteractionPhase={guided.isInteractionPhase}
        anotherClueEnabled={guided.anotherClueEnabled}
        hintEntry={guided.hintEntry}
        showHowAvailable={guided.showHowAvailable}
        resolvedExplanation={guided.resolvedExplanation}
        explanationOpen={guided.explanationOpen}
        onDismiss={guided.dismissOverlay}
        onAnotherClue={guided.anotherClue}
        onReadAloud={() =>
          guidedHintReadAloud(guided.hintEntry, activityId, guided.readAloudTrackLevel)
        }
        onShowHow={guided.openExplanation}
      />
      <GuidedHintHelpButton visible={guided.showHelpButton} onClick={guided.requestHintManually} />

      <h2 style={titleStyle}>{title}</h2>
      {content.showDescription && instructions ? <p style={instructionsStyle}>{instructions}</p> : null}

      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: aspect,
          borderRadius: 16,
          overflow: 'hidden',
          background: scene.background?.length ? 'transparent' : '#e0f2fe',
          border: '1px solid var(--color-border)',
        }}
      >
        <SceneSvg scene={scene} vbW={vbW} vbH={vbH} />

        {hotspots.map((h) => {
          const c = hotspotCenter(h);
          const left = ((c.x - vbX) / vbW) * 100;
          const top = ((c.y - vbY) / vbH) * 100;
          let sizePctW;
          let sizePctH;
          if (h.shape === 'rect') {
            sizePctW = Math.max(((h.width ?? 0) / vbW) * 100, 10);
            sizePctH = Math.max(((h.height ?? 0) / vbH) * 100, 10);
          } else {
            const rawW = ((h.r ?? 6) * 2) / vbW;
            const rawH = ((h.r ?? 6) * 2) / vbH;
            const sideW = Math.max(rawW, MIN_CIRCLE_SIDE_PCT / 100);
            sizePctW = sideW * 100;
            sizePctH = sideW * (vbW / vbH) * 100;
          }
          const state = tapped[h.id];
          const stateColor =
            state === 'correct'
              ? 'rgba(34, 197, 94, 0.85)'
              : state === 'wrong'
                ? 'rgba(239, 68, 68, 0.85)'
                : 'rgba(15, 23, 42, 0.32)';
          const fillBg =
            state === 'correct'
              ? 'rgba(34, 197, 94, 0.22)'
              : state === 'wrong'
                ? 'rgba(239, 68, 68, 0.18)'
                : 'rgba(255,255,255,0.18)';
          return (
            <motion.button
              key={h.id}
              type="button"
              onClick={() => handleTap(h)}
              aria-label={`Hotspot: ${h.label}`}
              animate={state === 'wrong' ? { x: [0, -4, 4, -2, 2, 0] } : { x: 0 }}
              transition={{ duration: 0.35 }}
              whileTap={{ scale: 0.95 }}
              style={{
                position: 'absolute',
                left: `${left}%`,
                top: `${top}%`,
                width: `${sizePctW}%`,
                height: `${sizePctH}%`,
                transform: 'translate(-50%, -50%)',
                borderRadius: h.shape === 'rect' ? 6 : '50%',
                background: fillBg,
                border: `2px ${state ? 'solid' : 'dashed'} ${stateColor}`,
                cursor: state === 'correct' ? 'default' : 'pointer',
                color: 'var(--color-text)',
                fontWeight: 700,
                fontSize: 'clamp(1.15rem, 5.5vw, 1.65rem)',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                touchAction: 'manipulation',
              }}
              disabled={state === 'correct'}
            >
              {state === 'correct' ? '✓' : h.emoji || ''}
            </motion.button>
          );
        })}
      </div>

      {feedback ? (
        <p
          style={{
            ...(feedback.type === 'correct' ? feedbackCorrect : feedbackIncorrect),
            marginTop: 14,
          }}
          role="status"
        >
          {feedback.message}
        </p>
      ) : null}

      <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text)', opacity: 0.8 }}>
          Found {correctTappedCount} of {goalType === 'count' ? Math.min(goalCount, correctHotspots.length) : correctHotspots.length}
        </span>
        {missCount > 0 ? (
          <button
            type="button"
            style={{ ...secondaryBtn, width: 'auto', flex: '0 0 auto', minHeight: 40, padding: '8px 14px' }}
            onClick={reset}
          >
            Reset
          </button>
        ) : null}
      </div>

    </div>
  );
}
